import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Trash2, Plus, Edit2, GraduationCap } from 'lucide-react';
import axios from 'axios';

const Escolaridade = () => {
  const [escolaridades, setEscolaridades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    ordem: ''
  });

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    carregarEscolaridades();
  }, []);

  const carregarEscolaridades = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/cadastros/escolaridade`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEscolaridades(response.data);
    } catch (error) {
      console.error('Erro ao carregar escolaridades:', error);
      alert('Erro ao carregar escolaridades');
    } finally {
      setLoading(false);
    }
  };

  const salvar = async () => {
    try {
      if (!formData.nome) {
        alert('Nome é obrigatório');
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        ordem: formData.ordem === '' ? 0 : parseInt(formData.ordem) || 0
      };

      if (editando) {
        await axios.put(
          `${API}/api/cadastros/escolaridade/${editando}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/api/cadastros/escolaridade`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setFormData({ nome: '', descricao: '', ordem: '' });
      setEditando(null);
      carregarEscolaridades();
      alert('Escolaridade salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert(error.response?.data?.detail || 'Erro ao salvar escolaridade');
    } finally {
      setLoading(false);
    }
  };

  const editar = (esc) => {
    setFormData({
      nome: esc.nome,
      descricao: esc.descricao || '',
      ordem: esc.ordem.toString()
    });
    setEditando(esc.id);
  };

  const excluir = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta escolaridade?')) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/cadastros/escolaridade/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      carregarEscolaridades();
      alert('Escolaridade excluída com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert(error.response?.data?.detail || 'Erro ao excluir escolaridade');
    } finally {
      setLoading(false);
    }
  };

  const cancelar = () => {
    setFormData({ nome: '', descricao: '', ordem: '' });
    setEditando(null);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-2">
        <GraduationCap className="w-8 h-8 text-blue-600" />
        <h1 className="text-3xl font-bold">Cadastro de Escolaridade</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulário */}
        <Card>
          <CardHeader>
            <CardTitle>{editando ? 'Editar' : 'Nova'} Escolaridade</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Nome *</Label>
              <Input
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Ensino Médio Completo"
              />
            </div>

            <div>
              <Label>Descrição</Label>
              <Input
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descrição opcional"
              />
            </div>

            <div>
              <Label>Ordem de Exibição</Label>
              <Input
                type="number"
                value={formData.ordem}
                onChange={(e) => setFormData({ ...formData, ordem: e.target.value })}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={salvar} disabled={loading} className="flex-1">
                <Plus className="w-4 h-4 mr-2" />
                {editando ? 'Atualizar' : 'Adicionar'}
              </Button>
              {editando && (
                <Button onClick={cancelar} variant="outline">
                  Cancelar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Lista */}
        <Card>
          <CardHeader>
            <CardTitle>Escolaridades Cadastradas ({escolaridades.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {escolaridades.map((esc) => (
                <div
                  key={esc.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                >
                  <div className="flex-1">
                    <p className="font-semibold">{esc.nome}</p>
                    {esc.descricao && (
                      <p className="text-sm text-gray-600">{esc.descricao}</p>
                    )}
                    <p className="text-xs text-gray-500">Ordem: {esc.ordem}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => editar(esc)}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => excluir(esc.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}

              {escolaridades.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  Nenhuma escolaridade cadastrada
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Escolaridade;
